import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Badge, BadgeProps } from '@mantine/core';
import {
	DetailsDiv,
	InfoDiv,
	UniqueDiv,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import styled from 'styled-components';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { TableFilterCheckboxSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/TableFilterCheckboxSelect';

type BadgeMapping = Record<string, { backgroundColor: string; text: string; fontColor: string }>;

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	badgeMapping: BadgeMapping;
	showFooter?: boolean;
	showFilter?: boolean;
	footerValue?: string | number;
	text?: string;
	headerText?: string;
	filterText?: string;
	badgeLimit?: number;
	header?: string | (() => React.ReactNode);
	accessorKey?: string;
	enableColumnActions?: boolean;
	filterVariant?: string;
	enableSorting?: boolean;
	mantineFilterMultiSelectProps?: {
		data: { label: string; value: string }[];
	};
};

function isArrayOfRecords(value: unknown): value is Array<Record<string, string>> {
	return Array.isArray(value);
}

export const createBadgeColumn = <T extends RecordAny>({
	badgeMapping,
	showFooter = false,
	footerValue,
	text,
	headerText,
	filterText,
	badgeLimit,
	enableSorting = true,
	mantineFilterMultiSelectProps,
	...columnProps
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		...columnProps,
		header: headerText
			? createSortableTextHeader(headerText, enableSorting)
			: columnProps.header,
		columnLabel: columnProps.columnLabel ?? headerText ?? '',
		enableSorting,
		mantineFilterMultiSelectProps,
		cell: ({ cell }) => {
			// this can be an array or a string
			const value = cell.getValue() as keyof typeof badgeMapping;

			if (isArrayOfRecords(value) && value.length) {
				const valuesToMap = value.slice(0, badgeLimit) as unknown as Array<
					Record<string, string>
				>;

				return (
					<>
						{valuesToMap.map((v) => {
							const badgeInfo = badgeMapping[v.name];

							if (!badgeInfo) {
								return (
									<BadgeWrapper key={v.name} bg="blue.1" c="blue.9" fw={400}>
										{v.name}
									</BadgeWrapper>
								);
							}

							return (
								<BadgeWrapper
									key={v.name}
									bg={badgeInfo.backgroundColor}
									c={badgeInfo.fontColor}
									fw={400}
								>
									{badgeInfo.text}
								</BadgeWrapper>
							);
						})}
					</>
				);
			}

			const badgeInfo = badgeMapping[value];

			if (!badgeInfo) {
				return null;
			}

			return (
				<Badge bg={badgeInfo.backgroundColor} c={badgeInfo.fontColor} fw={400}>
					{badgeInfo.text}
				</Badge>
			);
		},
		filter: columnProps.showFilter
			? ({ column }) => (
					<TableFilterCheckboxSelect
						column={column as ExtendedColumn<unknown>}
						filterText={filterText}
						multiple
					/>
				)
			: undefined,
		footer: showFooter
			? () => (
					<DetailsDiv>
						<UniqueDiv>{text}</UniqueDiv>
						<InfoDiv>{footerValue}</InfoDiv>
					</DetailsDiv>
				)
			: undefined,
		...columnProps,
	};
};

const BadgeWrapper = styled(Badge)<BadgeProps>`
	margin-right: 0.5rem;
`;
